import { useMemo, useState } from "react";
import { api, useLoad } from "../api";
import type { ActiveTrade } from "../types";
import { CATEGORY_LABEL, fmt } from "../lib/format";
import { linkHandler } from "../lib/router";

type Side = "all" | "buy" | "sell";
type Rules = "setup" | "canon";

const RULES_KEY = "insidera.tradesRules";

function loadRules(): Rules {
  try {
    return localStorage.getItem(RULES_KEY) === "canon" ? "canon" : "setup";
  } catch {
    return "setup";
  }
}

/**
 * Активные сделки: рынки, где по сетапу уже был вход и позиция ещё открыта (правила по умолчанию, те же, что в
 * бэктесте). Результат — по закрытию последнего торгового дня.
 */
export function ActiveTradesPage() {
  const [rules, setRulesState] = useState<Rules>(loadRules);
  const setRules = (r: Rules) => {
    setRulesState(r);
    try {
      localStorage.setItem(RULES_KEY, r);
    } catch {
      /* без сохранения выбора */
    }
  };
  const { data, error, loading } = useLoad((s) => api.activeTrades(rules, s), [rules]);
  const [side, setSide] = useState<Side>("all");
  const all = data ?? [];
  const rows = useMemo(
    () => all.filter((t) => side === "all" || (side === "buy" ? t.dir > 0 : t.dir < 0)),
    [all, side],
  );
  const buys = all.filter((t) => t.dir > 0).length;
  const winners = all.filter((t) => (t.pct ?? 0) > 0).length;
  const withPct = all.filter((t) => t.pct != null);
  const avg = withPct.length ? withPct.reduce((a, t) => a + (t.pct ?? 0), 0) / withPct.length : null;
  const lastDate = all.map((t) => t.lastDate).sort().at(-1) ?? null;

  return (
    <main className="page journal-page trades-page">
      <a className="back" href="/ru" onClick={linkHandler("/ru")}>
        ← Все рынки
      </a>
      <h1>Активные сделки</h1>
      <p className="lede">
        Рынки, где по сетапу уже был вход и позиция ещё открыта. Результат и расстояние до стопа — по закрытию{" "}
        {lastDate ? fmt.date(lastDate) : "последнего торгового дня"}.
      </p>

      <div className="list-tools">
        <div className="seg" role="group" aria-label="Правила входа">
          <span className="seg-label">Правила входа</span>
          <div className="segmented">
            {(
              [
                ["setup", "Сетап 4+ · 3 ATR"],
                ["canon", "Канон (линия тренда)"],
              ] as [Rules, string][]
            ).map(([v, t]) => (
              <button key={v} type="button" className={rules === v ? "active" : undefined} aria-pressed={rules === v} onClick={() => setRules(v)}>
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>
      {rules === "canon" ? (
        <p className="explain">
          Канон Уильямса, как в бэктесте: вход на пробое линии тренда после сетапа COT или WILLCO по тренду SMA52,
          начальный стоп — минимум (максимум) 17 торговых дней до входа, в крупном тренде стоп движется за минимумом
          (максимумом) 17 дней.
        </p>
      ) : (
        <div className="notice" role="note">
          <b>Сетап 4+ · 3 ATR</b> (правила по умолчанию). Вход без линии тренда — на открытии первого дня, когда
          опубликованы позиции за неделю, в которую балл сетапа впервые стал 4 или 5 (обычно вторник). Начальный стоп
          3 ATR, в крупном тренде — подтягивание к минимуму (максимуму) 17 дней; сетап в обратную сторону — разворот.
          После издержек: 2024–2026 — 308 сделок, 42 % прибыльных, прибыль/убытки 2,2; проверка на 2021–2023, не
          участвовавших в выборе, — 133 сделки, 42 %, прибыль/убытки 2,2.
        </div>
      )}

      {loading && <div className="skeleton">Считаем позиции всех рынков…</div>}
      {error && <div className="notice notice-error">Не удалось загрузить сделки: {error}</div>}

      {data && !loading && (
        <>
          <dl className="bt-tiles trades-tiles">
            <div>
              <dt>Открыто сделок</dt>
              <dd>{all.length}</dd>
            </div>
            <div>
              <dt>Покупки / продажи</dt>
              <dd>
                {buys} / {all.length - buys}
              </dd>
            </div>
            <div>
              <dt>Сейчас в плюсе</dt>
              <dd>
                {winners} из {all.length}
              </dd>
            </div>
            <div>
              <dt>Средний результат</dt>
              <dd className={avg == null ? "" : avg >= 0 ? "up" : "down"}>{fmt.signedPct(avg)}</dd>
            </div>
          </dl>

          <div className="list-tools">
            <div className="seg" role="group" aria-label="Сторона">
              <span className="seg-label">Сторона</span>
              <div className="segmented">
                {(
                  [
                    ["all", `Все ${all.length}`],
                    ["buy", `Покупки ${buys}`],
                    ["sell", `Продажи ${all.length - buys}`],
                  ] as [Side, string][]
                ).map(([v, t]) => (
                  <button key={v} type="button" className={side === v ? "active" : undefined} aria-pressed={side === v} onClick={() => setSide(v)}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="empty-state">
              {all.length === 0 ? "Открытых сделок сейчас нет: ни один сетап не дал входа, который ещё держится." : "Сделок в эту сторону нет."}
            </p>
          ) : (
            <div className="table-scroll">
              <table className="data-table trades-table">
                <thead>
                  <tr>
                    <th className="text">Инструмент</th>
                    <th className="text">Сторона</th>
                    <th>Вход</th>
                    <th>Сейчас</th>
                    <th>Результат</th>
                    <th>Стоп</th>
                    <th>Дней</th>
                    <th className="text">Сетап и вход</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <TradeRow key={t.code} t={t} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="explain">
            Сделка закрывается по стопу — тогда она пропадёт отсюда и появится в бэктесте.{" "}
            Данные обновляются каждую ночь.
          </p>
        </>
      )}
    </main>
  );
}

function TradeRow({ t }: { t: ActiveTrade }) {
  const href = `/ru/${encodeURIComponent(t.code)}`;
  const up = (t.pct ?? 0) >= 0;
  return (
    <tr>
      <td className="text">
        <a className="trade-inst" href={href} onClick={linkHandler(href)}>
          <b>{t.name}</b> <span className="ticker">{t.code}</span>
        </a>
        <small className="muted">{CATEGORY_LABEL[t.category] ?? t.category}</small>
      </td>
      <td className="text">
        <span className={`side-badge ${t.dir > 0 ? "up" : "down"}`}>
          <span aria-hidden="true">{t.dir > 0 ? "↗" : "↘"}</span> {t.dir > 0 ? "Покупка" : "Продажа"}
        </span>
        {t.reentry && <small className="muted">перевход</small>}
      </td>
      <td>
        {fmt.price(t.entryPrice)}
        <small className="muted">{fmt.date(t.entryDate)}</small>
      </td>
      <td>
        {fmt.price(t.lastPrice)}
        <small className="muted">
          {fmt.date(t.lastDate)}
          {t.preliminary ? " · предв." : ""}
        </small>
      </td>
      <td className={up ? "up" : "down"}>
        <b>{fmt.signedPct(t.pct)}</b>
        <small className="muted">{t.rub == null ? `${t.points > 0 ? "+" : ""}${fmt.price(t.points)} п.` : `${fmt.signed(t.rub)} ₽ на контракт`}</small>
      </td>
      <td>
        {fmt.price(t.stop)}
        <small className="muted">{t.stopDistPct == null ? "" : `до стопа ${fmt.pct(Math.abs(t.stopDistPct))}`}</small>
      </td>
      <td>{t.days}</td>
      <td className="text trade-why">
        {t.setupTitle && (
          <span>
            <span className="kind-chip">сетап {t.setupDate ? fmt.shortDate(t.setupDate) : ""}</span> {t.setupTitle}
          </span>
        )}
        {t.entryTitle && (
          <span title={t.entryDetail ?? undefined}>
            <span className="kind-chip">вход</span> {t.entryTitle}
          </span>
        )}
      </td>
    </tr>
  );
}
